/**
 * 店家時區（單一事實來源）
 * ============================================================
 *
 * 為什麼需要這支：
 *   預約的 date / time 在資料庫是「牆上時間字串」（'2026-09-15' / '14:00'），
 *   本身不帶時區，代表的是店家所在地的時間。但瀏覽器的 dayjs() / new Date()
 *   一律用「使用者裝置的時區」去解讀與顯示 —— 店家人在清邁（UTC+7）時，
 *   同一筆資料就會整個往前跑一小時，跨午夜還會整天錯位。
 *
 * 規則只有三條，套用時先問自己「這個值是什麼」：
 *
 *   1. 牆上時間（使用者輸入的、或 DB 裡的 date/time 字串）
 *        → wallTime('2026-09-15 14:00')        // 當成店家時區的 14:00
 *        → fromPicker(antdDayjsValue)          // 使用者在日期/時間選擇器挑的值
 *
 *   2. 絕對時刻（Mongo 的 createdAt、ISO 字串、FullCalendar 回傳的 Date）
 *        → inTz(value)  /  formatTz(value, 'YYYY-MM-DD HH:mm')
 *
 *   3. 現在幾點、今天是哪一天
 *        → now()  /  todayKey()
 *
 * ⚠️ 最常見的錯誤寫法是對「使用者挑的值」呼叫 .tz('Asia/Taipei')。
 *    .tz() 保留的是「瞬間」而改寫牆上時間 —— 使用者挑 00:00，在 UTC+7 會變成
 *    台灣的 01:00。這種情況要用 fromPicker()（內部是 .tz(tz, true)，保留牆上時間）。
 *
 * 時區來源：Business.timezone（預設 Asia/Taipei）。登入後由 AuthContext 呼叫
 * setBusinessTimezone() 寫入，未來要開海外店家不需要再改這裡的呼叫端。
 */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'

// 這三個外掛在專案裡被散落地 extend（有些檔案根本沒 extend 卻直接用 .tz()，
// 靠的是別的模組剛好先載入的巧合）。集中在這裡 extend 一次，dayjs 是模組單例，
// 只要這支被 src/index.js 匯入，全 App 都拿得到。
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

export const DEFAULT_TIMEZONE = 'Asia/Taipei'

let businessTimezone = DEFAULT_TIMEZONE
dayjs.tz.setDefault(businessTimezone)

/** 設定目前操作店家的時區（登入／切店時由 AuthContext 呼叫） */
export function setBusinessTimezone (tz) {
  businessTimezone = tz || DEFAULT_TIMEZONE
  dayjs.tz.setDefault(businessTimezone)
  return businessTimezone
}

/** 目前操作店家的時區（IANA 名稱） */
export function getBusinessTimezone () {
  return businessTimezone
}

/** 現在（店家時區）。取代所有的 dayjs() / moment() / new Date() */
export function now () {
  return dayjs().tz(businessTimezone)
}

/** 今天（店家時區）的 'YYYY-MM-DD' */
export function todayKey () {
  return now().format('YYYY-MM-DD')
}

/**
 * 牆上時間 → 店家時區的 dayjs。
 * 給 DB 的 date/time 字串、或任何「沒有時區資訊」的字串用。
 *   wallTime('2026-09-15')
 *   wallTime('2026-09-15 14:00')
 *   wallTime('14:00', 'HH:mm')
 */
export function wallTime (input, format) {
  return format
    ? dayjs.tz(input, format, businessTimezone)
    : dayjs.tz(input, businessTimezone)
}

/**
 * 絕對時刻 → 店家時區的 dayjs。
 * 給 Mongo 的 createdAt / updatedAt、帶 Z 或 offset 的 ISO 字串、
 * 以及 FullCalendar callback 給的 Date（v6 的 dateEnv.toDate() 回傳真實瞬間）用。
 */
export function inTz (input) {
  return dayjs(input).tz(businessTimezone)
}

/** inTz + format 的捷徑；空值回傳 fallback，省掉呼叫端一堆三元判斷 */
export function formatTz (input, pattern = 'YYYY-MM-DD HH:mm', fallback = '-') {
  if (input === null || input === undefined || input === '') return fallback
  const d = inTz(input)
  return d.isValid() ? d.format(pattern) : fallback
}

/**
 * 使用者在 antd DatePicker / TimePicker 挑的值 → 店家時區。
 *
 * 選擇器產生的是「瀏覽器時區的牆上時間」。使用者挑 14:00 就是要 14:00，
 * 所以保留牆上時間、只換時區（keepLocalTime = true）。
 * 千萬不要對這種值用 inTz()／.tz()，那會把 14:00 換算成 15:00。
 */
export function fromPicker (input) {
  if (!input) return null
  return dayjs(input).tz(businessTimezone, true)
}

/** 預設輸出：一個已經綁好時區的 dayjs 工廠，方便 `import tz from '...'` 後用 tz.now() */
const tzUtils = {
  DEFAULT_TIMEZONE,
  setBusinessTimezone,
  getBusinessTimezone,
  now,
  todayKey,
  wallTime,
  inTz,
  formatTz,
  fromPicker
}

export default tzUtils
